import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Container,
    Grid,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    Tooltip,
    Fab,
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Edit as EditIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import Header from './Header';
import DraggableSensorTile from './DraggableSensorTile';
import DashboardSettings from './DashboardSettings';
import ColorPickerPopup from './ColorPickerPopup';
import api from '../api/api';
import { useDashboardPreferences, TileSize } from '../context/DashboardPreferencesContext';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

// Grid sizing based on tile size
const getGridSize = (tileSize) => {
    switch (tileSize) {
        case TileSize.COMPACT:
            return { xs: 12, sm: 6, md: 4, lg: 3 };
        case TileSize.EXPANDED:
            return { xs: 12, sm: 12, md: 6, lg: 6 };
        case TileSize.NORMAL:
        default:
            return { xs: 12, sm: 6, md: 4, lg: 4 };
    }
};

function DashboardPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const {
        tileSize,
        isEditMode,
        toggleEditMode,
        setTileOrder,
        applySortAndFilter,
        getTileColor,
        setTileColor,
    } = useDashboardPreferences();

    const [rawSensorData, setRawSensorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [selectedZoneForColor, setSelectedZoneForColor] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchSensorData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/sensor/latest');
            setRawSensorData(response.data);
            setError('');
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
            setRawSensorData([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchSensorData();
        const interval = setInterval(fetchSensorData, AUTO_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchSensorData]);

    // Apply sorting and filtering based on preferences
    const sensorData = useMemo(() => {
        return applySortAndFilter(rawSensorData, i18n.language);
    }, [rawSensorData, applySortAndFilter, i18n.language]);

    // Get all available zones for settings
    const availableZones = useMemo(() => {
        return rawSensorData.map(item => item.zone);
    }, [rawSensorData]);

    // Get zone IDs for sortable context
    const zoneIds = useMemo(() => {
        return sensorData.map(item => item.zone || 'null');
    }, [sensorData]);

    const handleTileClick = (zone) => {
        navigate(`/graph/${zone || 'null'}`);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = zoneIds.indexOf(active.id);
            const newIndex = zoneIds.indexOf(over.id);
            const newOrder = arrayMove(zoneIds, oldIndex, newIndex);
            setTileOrder(newOrder);
        }
    };

    const handleColorPickerOpen = (zone) => {
        setSelectedZoneForColor(zone);
        setColorPickerOpen(true);
    };

    const handleColorPickerClose = () => {
        setColorPickerOpen(false);
        setSelectedZoneForColor(null);
    };

    const handleColorSelect = (color) => {
        if (selectedZoneForColor !== null) {
            setTileColor(selectedZoneForColor, color);
        }
    };

    const gridSize = getGridSize(tileSize);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Header title={t('common.dashboard')} />

            <Container sx={{ mt: 4, position: 'relative' }}>
                {/* Toolbar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                    <Tooltip title={t('common.dashboardSettings')}>
                        <IconButton onClick={() => setSettingsOpen(true)}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isEditMode ? t('common.doneEditing') : t('common.editLayout')}>
                        <IconButton
                            onClick={toggleEditMode}
                            color={isEditMode ? 'primary' : 'default'}
                        >
                            {isEditMode ? <CheckIcon /> : <EditIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {isEditMode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('common.editModeHint')}
                    </Alert>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sensorData.length > 0 ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={zoneIds} strategy={rectSortingStrategy}>
                            <Grid container spacing={tileSize === TileSize.COMPACT ? 1 : 3}>
                                {sensorData.map((zoneData) => (
                                    <Grid
                                        item
                                        xs={gridSize.xs}
                                        sm={gridSize.sm}
                                        md={gridSize.md}
                                        lg={gridSize.lg}
                                        key={zoneData.zone || 'null'}
                                    >
                                        <DraggableSensorTile
                                            zoneData={zoneData}
                                            onClick={handleTileClick}
                                            isEditMode={isEditMode}
                                            tileSize={tileSize}
                                            backgroundColor={getTileColor(zoneData.zone)}
                                            onColorPickerOpen={handleColorPickerOpen}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </SortableContext>
                    </DndContext>
                ) : (
                    <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                        {t('common.noData')}
                    </Typography>
                )}

                {/* Floating edit button for mobile */}
                <Fab
                    color={isEditMode ? 'primary' : 'default'}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        display: { xs: 'flex', md: 'none' },
                    }}
                    onClick={toggleEditMode}
                >
                    {isEditMode ? <CheckIcon /> : <EditIcon />}
                </Fab>
            </Container>

            <DashboardSettings
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                availableZones={availableZones}
            />

            <ColorPickerPopup
                open={colorPickerOpen}
                onClose={handleColorPickerClose}
                currentColor={selectedZoneForColor !== null ? getTileColor(selectedZoneForColor) : '#ffffff'}
                onColorSelect={handleColorSelect}
            />
        </Box>
    );
}

export default DashboardPage;
